import axios from "axios";
import { useContext, useEffect, useState, useRef } from "react";
import { Table, DatePicker, Select, Descriptions, Modal, Spin, Timeline, Tag, Typography, Tooltip, Button } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";

const { Text } = Typography;

export default function AppliedOnDuty() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    // Target row to highlight (from notification)
    const [targetOndutyId, setTargetOndutyId] = useState(null);
    const highlightRowRef = useRef(null);

    const [filters, setFilters] = useState({
        status: null,
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });

    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async (filterValues = filters) => {
        try {
            setLoading(true);

            const params = {};

            if (filterValues.status) params.status = filterValues.status;
            if (filterValues.month) params.month = filterValues.month;
            if (filterValues.year) params.year = filterValues.year;
            if (filterValues.fromDate)
                params.from_date = dayjs(filterValues.fromDate).format("YYYY-MM-DD");
            if (filterValues.toDate)
                params.to_date = dayjs(filterValues.toDate).format("YYYY-MM-DD");

            const res = await axios.get(
                `${backUrl}/api/hr/attendance/my-onduty-requests/`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                    params,
                }
            );

            let data = res.data.results || res.data;
            if (!Array.isArray(data)) data = [];

            // Apply client-side filters for 100% precision
            if (filterValues.month) {
                const m = Number(filterValues.month);
                data = data.filter((item) => {
                    const d = dayjs(item.start_time || item.created_at || item.date);
                    return d.isValid() && (d.month() + 1) === m;
                });
            }

            if (filterValues.year) {
                const y = Number(filterValues.year);
                data = data.filter((item) => {
                    const d = dayjs(item.start_time || item.created_at || item.date);
                    return d.isValid() && d.year() === y;
                });
            }

            if (filterValues.status) {
                data = data.filter((item) => item.status === filterValues.status);
            }

            if (filterValues.fromDate) {
                const from = dayjs(filterValues.fromDate).startOf("day");
                data = data.filter((item) => {
                    const d = dayjs(item.start_time || item.created_at || item.date);
                    return d.isValid() && (d.isAfter(from.subtract(1, "second")) || d.isSame(from, "day"));
                });
            }

            if (filterValues.toDate) {
                const to = dayjs(filterValues.toDate).endOf("day");
                data = data.filter((item) => {
                    const d = dayjs(item.start_time || item.created_at || item.date);
                    return d.isValid() && (d.isBefore(to.add(1, "second")) || d.isSame(to, "day"));
                });
            }

            setRequests(data);
            setPagination((prev) => ({ ...prev, current: 1, total: data.length }));
        } catch (error) {
            console.error("Error fetching On Duty requests:", error.response || error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ✅ Handle navigation from notifications - highlight specific on-duty row
    useEffect(() => {
        if (navigationParams?.ondutyId && navigationParams?.openModal) {
            console.log("🎯 AppliedOnDuty: Received navigation params:", navigationParams);
            setTargetOndutyId(navigationParams.ondutyId);
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Scroll to highlighted row after requests load
    useEffect(() => {
        if (targetOndutyId && requests.length > 0) {
            const targetIdNum = parseInt(targetOndutyId, 10);
            const found = requests.find((r) => r.id === targetIdNum);
            if (found) {
                console.log("🎯 AppliedOnDuty: Found target on-duty:", found);
                setTimeout(() => {
                    if (highlightRowRef.current) {
                        highlightRowRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 300);
            }
        }
    }, [targetOndutyId, requests]);

    const updateFilter = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchData(newFilters);
    };

    const clearFilters = () => {
        const reset = {
            status: null,
            month: null,
            year: null,
            fromDate: null,
            toDate: null,
        };
        setFilters(reset);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchData(reset);
    };

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        return statusColors[status.toUpperCase()] || "#faad14";
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "On Duty Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: (text, record) => (
                <>
                    <p>{text}</p>
                    <p>{record.check_in_out_mode}</p>

                    {(() => {
                        const raw = record?.lead_data || record?.lead_details || record?.external_lead_details;
                        if (!raw) return false;
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (Array.isArray(parsed)) return parsed.length > 0;
                            if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                            return !!parsed;
                        } catch (e) {
                            return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                        }
                    })() && (
                        <a
                            onClick={() => {
                                const leadData = record.lead_data || record.lead_details || record.external_lead_details;
                                let parsedLeads = leadData;
                                if (typeof leadData === "string") {
                                    try {
                                        parsedLeads = JSON.parse(leadData);
                                    } catch (e) {
                                        console.error("Failed to parse lead_data", e);
                                    }
                                }
                                const leadsArray = Array.isArray(parsedLeads) ? parsedLeads : [parsedLeads];
                                setSelectedLeadDetails(leadsArray);
                                setShowLead(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer", display: "block", marginTop: "4px" }}
                        >
                            Lead Details
                        </a>
                    )}
                </>
            ),
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 4,
                        }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 250,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) ||
                    (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude
                            },${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Start Time",
            dataIndex: "start_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "End Time",
            dataIndex: "end_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? String(text).charAt(0).toUpperCase() +
                        String(text).slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 380,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <Timeline style={{ marginTop: "12px", marginBottom: "-12px" }}>
                        {logs.map((log, index) => {
                            const statusColor =
                                log.status === "APPROVED"
                                    ? "green"
                                    : log.status === "REJECTED"
                                        ? "red"
                                        : "gray";

                            return (
                                <Timeline.Item key={index} color={statusColor} style={{ paddingBottom: "10px" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "nowrap" }}>
                                            <Text strong style={{ fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {log.approver_name}
                                            </Text>
                                            <Tag color={statusColor} style={{ marginRight: 0, whiteSpace: "nowrap", flexShrink: 0 }}>
                                                {log.status === "Cancelled" ? "Skipped (Cancelled)" : log.status}
                                            </Tag>
                                        </div>

                                        {/* Combined Action Details */}
                                        {(log.updated_at || log.remarks) && (
                                            <div style={{ marginTop: "2px" }}>
                                                {log.updated_at && (
                                                    <div style={{ color: "#595959", fontSize: "11px", whiteSpace: "nowrap" }}>
                                                        <Text strong style={{ fontSize: "11px", color: "#595959" }}>
                                                            {log.status === "REJECTED" ? "Rejected On: " : "On: "}
                                                        </Text>
                                                        {dayjs(log.updated_at).format("DD MMM YYYY [at] hh:mm A")}
                                                    </div>
                                                )}
                                                {log.remarks && (
                                                    <div style={{ fontSize: "12px", color: "#262626", wordBreak: "break-word" }}>
                                                        <Text strong>Remark: </Text>
                                                        <Text>{log.remarks}</Text>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Cancellation Block */}
                                        {(log.cancellation_approved_on || log.cancellation_rejected_on || log.cancellation_reason) && (
                                            <div style={{
                                                marginTop: "4px",
                                                paddingTop: "4px",
                                                borderTop: "1px dashed #e8e8e8"
                                            }}>
                                                {(log.cancellation_approved_on || log.cancellation_rejected_on) && (
                                                    <div style={{ color: "#595959", fontSize: "11px", whiteSpace: "nowrap" }}>
                                                        <Text strong style={{ fontSize: "11px", color: "#595959" }}>
                                                            Cancellation {log.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}
                                                        </Text>
                                                        {dayjs(log.cancellation_approved_on || log.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                                                    </div>
                                                )}
                                                {log.cancellation_reason && (
                                                    <div style={{ fontSize: "12px", color: "#262626", wordBreak: "break-word" }}>
                                                        <Text strong>Cancellation Remark: </Text>
                                                        <Text>{log.cancellation_reason}</Text>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                );
            },
        },
    ];

    const currentYear = dayjs().year();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

    return (
        <>
            {/* Lead Modal */}

            <Modal
                open={showLead}
                footer={null}
                onCancel={() => {
                    setShowLead(false);
                    setSelectedLeadDetails(null);
                }}
                width={700}
            >
                {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {selectedLeadDetails.map((lead, index) => (
                            <Descriptions
                                key={index}
                                bordered
                                column={1}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={selectedLeadDetails.length > 1 ? `Lead ${index + 1}` : null}
                            >
                                <Descriptions.Item label="Lead Number">
                                    <strong>{lead?.lead_no || lead?.lead_id || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {lead?.customer_name || lead?.name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {lead?.email || lead?.customer_email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {lead?.number || lead?.phone || lead?.customer_phone || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {lead?.project_name || lead?.project_id || (lead?.project && lead.project.title) || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: "#f6ffed",
                                            color: String(lead?.status?.title || lead?.status || "").toLowerCase().includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                            border: "1px solid #b7eb8f"
                                        }}
                                    >
                                        {lead?.status?.title || lead?.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        No lead details available
                    </div>
                )}
            </Modal>

            {/* Filters */}

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: 16,
                    alignItems: "center",
                    flexDirection: isMobile ? "column" : "row",
                }}
            >
                <Select
                    placeholder="Year"
                    value={filters.year}
                    allowClear
                    style={{ width: 130 }}
                    onChange={(val) => updateFilter("year", val)}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Month"
                    value={filters.month}
                    allowClear
                    style={{ width: 150 }}
                    onChange={(val) => updateFilter("month", val)}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index + 1} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    value={filters.fromDate}
                    format="DD/MM/YYYY"
                    style={{ width: 150 }}
                    onChange={(date) => updateFilter("fromDate", date)}
                />

                <DatePicker
                    placeholder="To Date"
                    value={filters.toDate}
                    format="DD/MM/YYYY"
                    style={{ width: 150 }}
                    onChange={(date) => updateFilter("toDate", date)}
                />

                <Select
                    placeholder="Status"
                    value={filters.status}
                    allowClear
                    style={{ width: 160 }}
                    onChange={(val) => updateFilter("status", val)}
                >
                    {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>

                <Button icon={<ReloadOutlined />} onClick={clearFilters}>
                    Clear
                </Button>
            </div>

            <Spin spinning={loading} size="large">
                <Table
                    columns={columns}
                    dataSource={requests.map((item, index) => ({
                        key: index,
                        ...item,
                    }))}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: requests.length,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                        onChange: (page, pageSize) => {
                            setPagination((prev) => {
                                if (prev.pageSize !== pageSize) {
                                    return { current: 1, pageSize, total: requests.length };
                                }
                                return { current: page, pageSize, total: requests.length };
                            });
                        },
                    }}
                    scroll={{ x: 1400 }}
                    rowClassName={(record) => {
                        const targetIdNum = parseInt(targetOndutyId, 10);
                        return record.id === targetIdNum ? "highlighted-row" : "";
                    }}
                    onRow={(record) => {
                        const targetIdNum = parseInt(targetOndutyId, 10);
                        return {
                            ref: record.id === targetIdNum ? highlightRowRef : null,
                        };
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            </Spin>
        </>
    );
}